import { useState, useEffect } from "react";
import axios from "axios";
import { Building2, Plus, Trash2, Edit2, MapPin, RotateCcw, AlertTriangle, Lock, Power, PowerOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger, SheetFooter } from "@/components/ui/sheet";
import { toast } from "sonner";
import { useLanguage } from "../LanguageContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const WarehouseManagement = () => {
  const { t } = useLanguage();
  const [warehouses, setWarehouses] = useState([]);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [formData, setFormData] = useState({
    warehouse_name: "",
    location: ""
  });
  
  // Reset modal state
  const [showResetModal, setShowResetModal] = useState(false);
  const [resetWarehouse, setResetWarehouse] = useState(null);
  const [resetPassword, setResetPassword] = useState("");
  const [isResetting, setIsResetting] = useState(false);

  useEffect(() => {
    fetchWarehouses();
  }, []);

  const fetchWarehouses = async () => {
    try {
      const response = await axios.get(`${API}/admin/warehouses`, { withCredentials: true });
      setWarehouses(response.data);
    } catch (error) {
      toast.error("Failed to load warehouses");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      await axios.post(`${API}/admin/warehouses`, formData, { withCredentials: true });
      toast.success("Warehouse created successfully");
      
      setFormData({ warehouse_name: "", location: "" });
      setIsSheetOpen(false);
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create warehouse");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this warehouse?")) return;
    
    try {
      await axios.delete(`${API}/admin/warehouses/${id}`, { withCredentials: true });
      toast.success("Warehouse deleted successfully");
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete warehouse");
    }
  };

  // Toggle warehouse enable/disable
  const handleToggleStatus = async (warehouse) => {
    const newStatus = !warehouse.is_active;
    try {
      await axios.patch(
        `${API}/admin/warehouses/${warehouse.id}/status?is_active=${newStatus}`,
        {},
        { withCredentials: true }
      );
      toast.success(`Warehouse ${newStatus ? 'enabled' : 'disabled'} successfully`);
      fetchWarehouses();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update warehouse status");
    }
  };

  // Warehouse Reset Handler
  const handleResetWarehouse = async () => {
    if (!resetPassword) {
      toast.error(t('admin.enterPassword'));
      return;
    }
    
    setIsResetting(true);
    try {
      const response = await axios.post(
        `${API}/admin/warehouses/${encodeURIComponent(resetWarehouse.warehouse_name)}/reset?password=${encodeURIComponent(resetPassword)}`,
        {},
        { withCredentials: true }
      );
      
      toast.success(`${t('admin.resetWarehouse')}: ${response.data.transactions_deleted} transactions deleted`);
      setShowResetModal(false);
      setResetWarehouse(null);
      setResetPassword("");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reset failed - check password");
    } finally {
      setIsResetting(false);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-outfit font-semibold text-stone-900">
                Warehouse Master Data
              </CardTitle>
              <CardDescription>Manage warehouse locations and facilities</CardDescription>
            </div>
            <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
              <SheetTrigger asChild>
                <Button
                  onClick={() => setFormData({ warehouse_name: "", location: "" })}
                  className="bg-lime-700 text-white hover:bg-lime-800"
                  data-testid="add-warehouse-btn"
                >
                  <Plus className="w-4 h-4 mr-2" />Add Warehouse
                </Button>
              </SheetTrigger>
              <SheetContent className="overflow-y-auto">
                <SheetHeader>
                  <SheetTitle>Add New Warehouse</SheetTitle>
                  <SheetDescription>Create a new warehouse location</SheetDescription>
                </SheetHeader>
                <form onSubmit={handleSubmit} className="space-y-6 mt-6">
                  <div className="space-y-2">
                    <Label htmlFor="warehouse_name">Warehouse Name *</Label>
                    <Input
                      id="warehouse_name"
                      value={formData.warehouse_name}
                      onChange={(e) => setFormData({ ...formData, warehouse_name: e.target.value })}
                      placeholder="e.g., Main Warehouse, Warehouse A"
                      required
                      data-testid="warehouse-name-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="location">Location</Label>
                    <Input
                      id="location"
                      value={formData.location}
                      onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                      placeholder="e.g., Amritsar, Punjab"
                      data-testid="warehouse-location-input"
                    />
                  </div>
                  <SheetFooter>
                    <Button type="submit" className="bg-lime-700 text-white hover:bg-lime-800" data-testid="save-warehouse-btn">
                      Create Warehouse
                    </Button>
                  </SheetFooter>
                </form>
              </SheetContent>
            </Sheet>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Warehouse Name</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Created</TableHead>
                <TableHead className="text-left">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {warehouses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} className="text-center py-8 text-stone-500">
                    No warehouses found. Add your first warehouse to get started.
                  </TableCell>
                </TableRow>
              ) : (
                warehouses.map((warehouse) => (
                  <TableRow key={warehouse.id} data-testid={`warehouse-row-${warehouse.id}`} className={warehouse.is_active === false ? 'opacity-60 bg-stone-50' : ''}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        <Building2 className={`w-4 h-4 ${warehouse.is_active === false ? 'text-stone-400' : 'text-stone-500'}`} />
                        {warehouse.warehouse_name}
                        {warehouse.is_active === false && (
                          <span className="text-xs px-2 py-0.5 bg-stone-200 text-stone-600 rounded">Disabled</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-stone-600">
                        {warehouse.location ? (
                          <>
                            <MapPin className="w-3 h-3" />
                            {warehouse.location}
                          </>
                        ) : (
                          <span className="text-stone-400">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-sm text-stone-500">
                      {new Date(warehouse.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-left">
                      <Button
                        onClick={() => handleToggleStatus(warehouse)}
                        variant="ghost"
                        size="sm"
                        className={warehouse.is_active === false ? "text-lime-600 hover:text-lime-700 mr-1" : "text-stone-500 hover:text-stone-700 mr-1"}
                        data-testid={`toggle-warehouse-${warehouse.id}`}
                        title={warehouse.is_active === false ? "Enable Warehouse" : "Disable Warehouse"}
                      >
                        {warehouse.is_active === false ? <Power className="w-4 h-4" /> : <PowerOff className="w-4 h-4" />}
                      </Button>
                      <Button
                        onClick={() => {
                          setResetWarehouse(warehouse);
                          setShowResetModal(true);
                        }}
                        variant="ghost"
                        size="sm"
                        className="text-amber-600 hover:text-amber-700 mr-1"
                        data-testid={`reset-warehouse-${warehouse.id}`}
                        title={t('admin.resetWarehouse')}
                      >
                        <RotateCcw className="w-4 h-4" />
                      </Button>
                      <Button
                        onClick={() => handleDelete(warehouse.id)}
                        variant="ghost"
                        size="sm"
                        className="text-rose-600 hover:text-rose-700"
                        data-testid={`delete-warehouse-${warehouse.id}`}
                        title="Delete Warehouse"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Reset Warehouse Modal */}
      {showResetModal && resetWarehouse && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-md w-full">
            <div className="p-4 border-b border-stone-200 flex items-center gap-3 bg-amber-50">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <AlertTriangle className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-amber-800">{t('admin.resetWarehouse')}</h3>
                <p className="text-sm text-amber-600">{resetWarehouse.warehouse_name}</p>
              </div>
            </div>
            <div className="p-6">
              <p className="text-sm text-stone-600 mb-4">
                {t('admin.resetConfirm')}
              </p>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="reset-password" className="flex items-center gap-2">
                    <Lock className="w-4 h-4" />
                    {t('admin.enterPassword')}
                  </Label>
                  <Input
                    id="reset-password"
                    type="password"
                    value={resetPassword}
                    onChange={(e) => setResetPassword(e.target.value)}
                    placeholder="Admin password"
                    data-testid="reset-password-input"
                  />
                </div>
              </div>
            </div>
            <div className="p-4 border-t border-stone-200 flex justify-end gap-3">
              <Button
                onClick={() => {
                  setShowResetModal(false);
                  setResetWarehouse(null);
                  setResetPassword("");
                }}
                variant="outline"
                data-testid="cancel-reset-btn"
              >
                {t('common.cancel')}
              </Button>
              <Button
                onClick={handleResetWarehouse}
                disabled={isResetting || !resetPassword}
                className="bg-amber-600 text-white hover:bg-amber-700"
                data-testid="confirm-reset-btn"
              >
                {isResetting ? t('common.loading') : t('admin.verify')}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default WarehouseManagement;